import React, { useMemo } from 'react'
import { Button } from './shadcn/Button'
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from './shadcn/command'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'
import tinycolor from 'tinycolor2'
import { useBasicEntities } from '../hooks/useBasicEntities'

const GROUP_ID_TRANSLATIONS: Record<string, string> = {
  cp: 'Česká pošta',
  dp: 'DHL',
  gls: 'GLS',
  ups: 'UPS',
  dpd: 'DPD',
  bpost: 'Bpost',
  dhl: 'DHL',
  wedo: 'WeDo',
  zasilkovna: 'Zásilkovna',
  shippypro: 'ShippyPro',
  ulozenka: 'Uloženka',
}

type Carrier = {
  id: string
  name: string
  color?: string | null
}

interface CarrierSelectorBaseProps {
  /** Placeholder text when no carrier is selected */
  placeholder?: string
  /** Class name for the trigger button */
  className?: string
  /** Whether the selector is disabled */
  disabled?: boolean
}

interface CarrierSelectorSingleProps extends CarrierSelectorBaseProps {
  /** Selection mode */
  mode: 'single'
  /** Currently selected carrier ID */
  value: string | undefined
  /** Callback when selection changes */
  onValueChange: (value: string | undefined) => void
}

interface CarrierSelectorMultiProps extends CarrierSelectorBaseProps {
  /** Selection mode */
  mode: 'multi'
  /** Currently selected carrier IDs */
  value: string[]
  /** Callback when selection changes */
  onValueChange: (value: string[]) => void
}

type CarrierSelectorProps = CarrierSelectorSingleProps | CarrierSelectorMultiProps

/**
 * CarrierSelector - A reusable component for selecting carriers
 * Supports both single-select and multi-select modes with grouped carriers
 */
export function CarrierSelector(props: CarrierSelectorProps) {
  const { mode, placeholder = 'Vyberte dopravce', className, disabled } = props
  const { carriers } = useBasicEntities()

  // Group carriers by their prefix (e.g., cp_, dp_, etc.)
  const groupedCarriers = useMemo(() => {
    return carriers.reduce(
      (acc, carrier) => {
        const groupId = carrier.id.split('_')[0]
        if (!acc[groupId]) {
          acc[groupId] = []
        }
        acc[groupId].push(carrier)
        return acc
      },
      {} as Record<string, Carrier[]>,
    )
  }, [carriers])

  // Carriers that are alone in their group (no grouping needed)
  const ungroupedCarriers = useMemo(() => {
    return Object.entries(groupedCarriers)
      .filter(([_, groupCarriers]) => groupCarriers.length === 1)
      .map(([_, groupCarriers]) => groupCarriers[0])
  }, [groupedCarriers])

  // Carriers that have multiple items in their group
  const groupedCarriersWithMultiple = useMemo(() => {
    return Object.entries(groupedCarriers)
      .filter(([_, groupCarriers]) => groupCarriers.length > 1)
      .sort(([a], [b]) => a.localeCompare(b))
  }, [groupedCarriers])

  const getCarrierBackgroundColor = (carrier: Carrier) => {
    return tinycolor.mix(tinycolor(carrier.color || 'rgb(150, 150, 150)'), '#fff', 80).toString()
  }

  // Render for multi-select mode
  if (mode === 'multi') {
    const { value: selectedIds, onValueChange } = props as CarrierSelectorMultiProps

    const getDisplayText = () => {
      if (selectedIds.length === 0) return 'Vše'
      if (selectedIds.length === 1) {
        return carriers.find(c => c.id === selectedIds[0])?.name || 'Vše'
      }
      return `${selectedIds.length} vybráno`
    }

    const handleSelectAll = () => {
      onValueChange([])
    }

    const handleToggleCarrier = (carrierId: string) => {
      const isSelected = selectedIds.includes(carrierId)
      const updated = isSelected ? selectedIds.filter(id => id !== carrierId) : [...selectedIds, carrierId]
      onValueChange(updated)
    }

    const handleToggleGroup = (groupIds: string[]) => {
      const allSelected = groupIds.every(id => selectedIds.includes(id))
      const updated = allSelected
        ? selectedIds.filter(id => !groupIds.includes(id))
        : Array.from(new Set([...selectedIds, ...groupIds]))
      onValueChange(updated)
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-full justify-between bg-gray-100 ${className || ''}`}
            size="lg"
            disabled={disabled}>
            {getDisplayText()}
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder="Hledat dopravce..." />
            <CommandList>
              <CommandEmpty>Žádné výsledky.</CommandEmpty>
              <CommandGroup>
                <CommandItem onSelect={handleSelectAll} className="cursor-pointer">
                  Vše
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              {ungroupedCarriers.map(carrier => {
                const isSelected = selectedIds.includes(carrier.id)
                return (
                  <CommandItem
                    key={carrier.id}
                    onSelect={() => handleToggleCarrier(carrier.id)}
                    className="cursor-pointer"
                    style={{ backgroundColor: getCarrierBackgroundColor(carrier) }}>
                    <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    {carrier.name}
                  </CommandItem>
                )
              })}
              {groupedCarriersWithMultiple.map(([groupId, groupCarriers]) => {
                const groupIds = groupCarriers.map(c => c.id)
                const allSelected = groupIds.every(id => selectedIds.includes(id))
                return (
                  <CommandGroup key={groupId}>
                    <div
                      cmdk-group-heading
                      className="px-2 py-1.5 text-xs font-medium cursor-pointer"
                      onClick={() => handleToggleGroup(groupIds)}>
                      {GROUP_ID_TRANSLATIONS[groupId] || groupId}
                    </div>
                    {groupCarriers.map(carrier => {
                      const isSelected = selectedIds.includes(carrier.id)
                      return (
                        <CommandItem
                          key={carrier.id}
                          onSelect={() => handleToggleCarrier(carrier.id)}
                          className="cursor-pointer"
                          style={{ backgroundColor: getCarrierBackgroundColor(carrier) }}>
                          <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                          {carrier.name}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                )
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  // Render for single-select mode
  const { value: selectedId, onValueChange } = props as CarrierSelectorSingleProps

  const getDisplayText = () => {
    if (!selectedId) return placeholder
    return carriers.find(c => c.id === selectedId)?.name || placeholder
  }

  const handleSelectCarrier = (carrierId: string | undefined) => {
    onValueChange(carrierId)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`w-full justify-between bg-gray-100 ${className || ''}`}
          size="lg"
          disabled={disabled}>
          {getDisplayText()}
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Hledat dopravce..." />
          <CommandList>
            <CommandEmpty>Žádné výsledky.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => handleSelectCarrier(undefined)} className="cursor-pointer">
                {placeholder === 'Vyberte dopravce' ? 'Vše' : placeholder}
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            {ungroupedCarriers.map(carrier => {
              const isSelected = selectedId === carrier.id
              return (
                <CommandItem
                  key={carrier.id}
                  onSelect={() => handleSelectCarrier(carrier.id)}
                  className="cursor-pointer"
                  style={{ backgroundColor: getCarrierBackgroundColor(carrier) }}>
                  <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                  {carrier.name}
                </CommandItem>
              )
            })}
            {groupedCarriersWithMultiple.map(([groupId, groupCarriers]) => (
              <CommandGroup key={groupId}>
                <div cmdk-group-heading className="px-2 py-1.5 text-xs font-medium">
                  {GROUP_ID_TRANSLATIONS[groupId] || groupId}
                </div>
                {groupCarriers.map(carrier => {
                  const isSelected = selectedId === carrier.id
                  return (
                    <CommandItem
                      key={carrier.id}
                      onSelect={() => handleSelectCarrier(carrier.id)}
                      className="cursor-pointer"
                      style={{ backgroundColor: getCarrierBackgroundColor(carrier) }}>
                      <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                      {carrier.name}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default CarrierSelector
